import { useState } from 'react';
import { Crosshair, Minus, Plus, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MAP_VIEW_BOX } from './map-scale';
import { useMapViewport } from './use-map-viewport';
import type { PaintedCountry } from './types';

interface WorldMapProps {
  countries: readonly PaintedCountry[];
  /** Countries named directly on the map, strongest first. */
  labelled: readonly PaintedCountry[];
  formatValue: (value: number) => string;
  contributorsLabel: string;
  shareLabel: string;
  zoomInLabel: string;
  zoomOutLabel: string;
  resetLabel: string;
  fitLabel: string;
}

const fillOfStep = (step: number) =>
  step === 0 ? 'hsl(var(--map-land))' : `hsl(var(--map-step-${step}))`;

/**
 * The contributor map: every African country is drawn, whether it contributed
 * or not, and the reader can zoom, pan and open a country. The outlines ship
 * with the application, so the map owes nothing to a tile provider.
 */
export function WorldMap({
  countries,
  labelled,
  formatValue,
  contributorsLabel,
  shareLabel,
  zoomInLabel,
  zoomOutLabel,
  resetLabel,
  fitLabel,
}: WorldMapProps) {
  const [focused, setFocused] = useState<string | null>(null);
  const { viewport, isPanning, isZoomed, zoomIn, zoomOut, reset, focusOn, fitTo, handlers } =
    useMapViewport();
  const focusedCountry = countries.find((country) => country.iso2 === focused) ?? null;
  /** Strokes and labels must not thicken when the view zooms in. */
  const unzoom = 1 / viewport.scale;
  /** Where the countries that carry contributions are, for the fit control. */
  const labelledAnchors = countries
    .filter((country) => country.value > 0)
    .map((country) => country.anchor);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/40 bg-card">
      <svg
        viewBox={`${MAP_VIEW_BOX.x} ${MAP_VIEW_BOX.y} ${MAP_VIEW_BOX.width} ${MAP_VIEW_BOX.height}`}
        className={cn('h-[420px] w-full touch-none', isPanning ? 'cursor-grabbing' : 'cursor-grab')}
        role="img"
        aria-label={contributorsLabel}
        {...handlers}
      >
        <g
          style={{
            transform: `scale(${viewport.scale}) translate(${viewport.x}px, ${viewport.y}px)`,
            transformOrigin: `${MAP_VIEW_BOX.x + MAP_VIEW_BOX.width / 2}px ${MAP_VIEW_BOX.y + MAP_VIEW_BOX.height / 2}px`,
            transition: isPanning ? 'none' : 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <g strokeLinejoin="round">
            {countries.map((country) => {
              const isFocused = country.iso2 === focused;
              const isActive = country.value > 0;
              return (
                <path
                  key={country.iso2}
                  d={country.path}
                  fill={fillOfStep(country.step)}
                  stroke="hsl(var(--map-border))"
                  strokeWidth={(isFocused ? 0.5 : 0.18) * unzoom}
                  fillOpacity={focused && !isFocused ? 0.55 : 1}
                  className={cn('transition-[fill-opacity] duration-200', isActive && 'cursor-pointer')}
                  tabIndex={isActive ? 0 : -1}
                  role={isActive ? 'button' : undefined}
                  aria-label={isActive ? `${country.name} : ${formatValue(country.value)}` : undefined}
                  onMouseEnter={() => setFocused(country.iso2)}
                  onMouseLeave={() =>
                    setFocused((current) => (current === country.iso2 ? null : current))
                  }
                  onFocus={() => setFocused(country.iso2)}
                  onBlur={() => setFocused((current) => (current === country.iso2 ? null : current))}
                  onClick={() => isActive && focusOn(country.anchor.x, country.anchor.y)}
                  onKeyDown={(event) => {
                    if (isActive && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      focusOn(country.anchor.x, country.anchor.y);
                    }
                  }}
                />
              );
            })}
          </g>

          <g className="pointer-events-none">
            {labelled.map((country) => (
              <g key={country.iso2}>
                <circle
                  cx={country.anchor.x}
                  cy={country.anchor.y}
                  r={2.4 * unzoom}
                  fill="hsl(var(--primary))"
                  className="map-pulse"
                />
                <circle
                  cx={country.anchor.x}
                  cy={country.anchor.y}
                  r={0.9 * unzoom}
                  fill="hsl(var(--foreground))"
                  fillOpacity={0.75}
                />
                <text
                  x={country.anchor.x}
                  y={country.anchor.y - 2 * unzoom}
                  textAnchor="middle"
                  fill="hsl(var(--foreground))"
                  fontSize={3.4 * unzoom}
                  fontWeight={600}
                  stroke="hsl(var(--card))"
                  strokeWidth={unzoom}
                  className="[paint-order:stroke]"
                >
                  {country.name}
                </text>
              </g>
            ))}
          </g>
        </g>
      </svg>

      <div className="absolute right-3 top-3 flex flex-col gap-1.5">
        <MapButton label={zoomInLabel} onClick={zoomIn}>
          <Plus className="h-3.5 w-3.5" />
        </MapButton>
        <MapButton label={zoomOutLabel} onClick={zoomOut}>
          <Minus className="h-3.5 w-3.5" />
        </MapButton>
        <MapButton label={fitLabel} onClick={() => fitTo(labelledAnchors)}>
          <Crosshair className="h-3.5 w-3.5" />
        </MapButton>
        {isZoomed && (
          <MapButton label={resetLabel} onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" />
          </MapButton>
        )}
      </div>

      {focusedCountry && focusedCountry.value > 0 && (
        <div
          role="status"
          className="pointer-events-none absolute left-4 top-4 rounded-2xl border border-border/50 bg-background/92 px-4 py-3 shadow-lg backdrop-blur-sm"
        >
          <p className="text-sm font-semibold text-foreground">{focusedCountry.reportedName}</p>
          <p className="mt-1 text-sm text-foreground/80">
            {formatValue(focusedCountry.value)} · {contributorsLabel}
          </p>
          <p className="text-xs text-muted-foreground">
            {(focusedCountry.share * 100).toFixed(1)}% · {shareLabel}
          </p>
        </div>
      )}
    </div>
  );
}

function MapButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-7 w-7 items-center justify-center rounded-full border border-border/50 bg-background/80 text-foreground/70 backdrop-blur-sm transition-colors hover:bg-background hover:text-foreground"
    >
      {children}
    </button>
  );
}
